import { useAuth } from '@clerk/react'
import * as Tooltip from '@radix-ui/react-tooltip'
import type { ColumnDef } from '@tanstack/react-table'
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { Loader2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useLocation } from '#router'
import { FeaturebaseBugButton } from '@/components/featurebase-bug-button'
import {
  ProblemListProvider,
  useProblemListsOptional,
} from '@/components/problem-lists/problem-list-context'
import { ProblemListSidebar } from '@/components/problem-lists/problem-list-sidebar'
import { StarDropdown } from '@/components/problem-lists/star-dropdown'
import { DifficultyCell } from '@/components/problems/difficulty-cell'
import { ProblemsFilterPopover } from '@/components/problems/problems-filter-popover'
import { ProblemsSearchBar } from '@/components/problems/problems-search-bar'
import { ProblemsSortPopover } from '@/components/problems/problems-sort-popover'
import { TopicPills } from '@/components/problems/topic-pills'
import {
  applyFilters,
  type BrowseProblem,
  createProblemsFuse,
  deriveAvailableDifficulties,
  deriveAvailableTopics,
  type SortOption,
  searchProblems,
  sortProblems,
} from '@/components/problems/types'
import { ActivityStats } from '@/components/ui/activity-stats'
import { ProgressCalendar } from '@/components/ui/progress-calendar'
import { ProgressStatusIcon } from '@/components/ui/progress-status-icon'
import { ProgressSummary } from '@/components/ui/progress-summary'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { useIsMobile } from '@/hooks/use-mobile'
import { useProblemsState } from '@/hooks/use-problems-state'
import { getInterviewingioUrl } from '@/lib/interviewingio'
import { isSolvedStatus } from '@/lib/progress-status'
import type { ListRef } from '@/lib/url-state'
import { cn } from '@/lib/utils'
import '@/styles/problems-table.css'
import { trpc } from '../lib/trpc.js'
import { MobileProblems } from './mobile-problems'
import { ShuffleButton } from './shuffle-button'

const PROBLEMS_SORT_OPTIONS = ['difficulty', 'questionId', 'status']

const PROBLEMS_SORT_POPOVER_OPTIONS = [
  { value: 'difficulty', label: 'Difficulty' },
  { value: 'questionId', label: 'Question ID' },
  { value: 'status', label: 'Status' },
]

export default function ProblemsTable() {
  const isMobile = useIsMobile()
  if (isMobile) return <MobileProblems />

  return (
    <ProblemListProvider>
      <ProblemsTableContent />
    </ProblemListProvider>
  )
}

function ProblemsTableContent() {
  const listCtx = useProblemListsOptional()

  // Fetch problem list (public, no auth needed)
  const { data: problems, isLoading } = trpc.problems.browse.useQuery()

  // Check auth status
  const { isSignedIn } = useAuth()

  const interviewingioFlag = useFeatureFlag('enable-interviewingio', {
    skipAdminBypass: true,
  })

  // Fetch user progress (only when signed in)
  const { data: userProgress } = trpc.problems.getUserProgress.useQuery(
    undefined,
    { enabled: !!isSignedIn },
  )

  // Fetch activity calendar data (only when signed in)
  const { data: activityData } = trpc.problems.getActivityCalendar.useQuery(
    undefined,
    { enabled: !!isSignedIn },
  )

  // Fetch activity stats — distinct problems solved per time period
  const { data: activityStats } = trpc.problems.getActivityStats.useQuery(
    undefined,
    { enabled: !!isSignedIn },
  )

  // Build list refs for the hook
  const userListRefs: ListRef[] = useMemo(
    () =>
      listCtx
        ? listCtx.lists.map((l) => ({
            id: l.id,
            name: l.name,
            isSystem: l.isSystem,
          }))
        : [],
    [listCtx?.lists, listCtx],
  )

  // Derive available filter values from data
  const availableTopics = useMemo(
    () => deriveAvailableTopics(problems ?? []),
    [problems],
  )

  const availableDifficulties = useMemo(
    () => deriveAvailableDifficulties(problems ?? []),
    [problems],
  )

  const availableLists = useMemo(() => {
    if (!listCtx) return undefined
    return [...listCtx.lists]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((l) => ({ id: l.id, name: l.name }))
  }, [listCtx?.lists, listCtx])

  // Filter/sort/search state (synced to URL + sessionStorage)
  const {
    filters,
    sortBy,
    sortDir,
    searchQuery,
    matchMode,
    showTags,
    missingLists,
    setSortBy,
    setSortDir,
    setSearchQuery,
    setMatchMode,
    setShowTags,
    toggleFilterValue,
    resetFilters,
    setFilterOperator,
  } = useProblemsState(
    'bctci-browse-table',
    userListRefs,
    availableTopics,
    PROBLEMS_SORT_OPTIONS,
  )

  // Dismissible banner for missing lists from URL
  const [dismissed, setDismissed] = useState(false)

  // Fuse.js instance
  const fuse = useMemo(() => createProblemsFuse(problems ?? []), [problems])

  // Filtered data
  const filteredProblems = useMemo(() => {
    if (!problems) return []

    // Start with search
    const searched = searchProblems(problems, fuse, searchQuery)

    // Apply filters
    return applyFilters(
      searched,
      filters,
      matchMode,
      userProgress?.progress,
      listCtx?.memberships,
    )
  }, [
    problems,
    searchQuery,
    fuse,
    filters,
    matchMode,
    userProgress,
    listCtx?.memberships,
  ])

  // Sorted data
  const sortedProblems = useMemo(
    () =>
      sortProblems(
        filteredProblems,
        sortBy as SortOption,
        sortDir,
        userProgress?.progress,
      ),
    [filteredProblems, sortBy, sortDir, userProgress],
  )

  // Navigation
  const { navigate } = useLocation()

  // Column definitions
  const columns: ColumnDef<BrowseProblem>[] = useMemo(
    () => [
      {
        accessorKey: 'title',
        header: 'Title',
        cell: ({ row }) => {
          const status = isSignedIn
            ? userProgress?.progress[row.original.id]
            : undefined
          const via = userProgress?.solvedVia?.[row.original.id]
          return (
            <Link
              href={`/editor?problem=${row.original.slug}`}
              className="block"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="inline-flex items-center gap-2 font-medium">
                {isSignedIn && (
                  <span className="inline-flex w-4 shrink-0 justify-center">
                    <ProgressStatusIcon status={status} viaTitle={via?.title} />
                  </span>
                )}
                <span>
                  {row.original.externalId && (
                    <span className="text-muted-foreground">
                      {Number(row.original.externalId)}.{' '}
                    </span>
                  )}
                  {row.original.title}
                </span>
              </span>
              {showTags && row.original.topics.length > 0 && (
                <div
                  className={cn(
                    'mt-1.5 flex flex-wrap gap-1.5',
                    isSignedIn && 'ml-6',
                  )}
                >
                  {row.original.topics.map((topic) => (
                    <span
                      key={topic.slug}
                      className="bg-muted text-muted-foreground rounded px-2 py-0.5 text-xs"
                    >
                      {topic.name}
                    </span>
                  ))}
                </div>
              )}
            </Link>
          )
        },
      },
      ...(interviewingioFlag.isLoaded && interviewingioFlag.enabled
        ? [
            {
              id: 'interviewingio' as const,
              header: 'AI Interviewer',
              size: 40,
              cell: ({ row }: { row: { original: BrowseProblem } }) => (
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <a
                      href={getInterviewingioUrl(row.original.slug)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      className="flex items-center justify-center"
                    >
                      <img
                        src="/icons/interviewing-io-sparkle-comment.webp"
                        alt="Professional interview"
                        className="h-5 w-5"
                      />
                    </a>
                  </Tooltip.Trigger>
                  <Tooltip.Content
                    side="top"
                    align="center"
                    className="border-border bg-background text-foreground rounded-md border px-3 py-1.5 text-sm font-medium shadow-md"
                  >
                    Get asked this question by our AI Interviewer
                    <Tooltip.Arrow className="fill-background" />
                  </Tooltip.Content>
                </Tooltip.Root>
              ),
            },
          ]
        : []),
      {
        id: 'difficulty',
        header: () => <span className="text-right block">Difficulty</span>,
        size: 80,
        cell: ({ row }) => (
          <DifficultyCell difficulty={row.original.difficulty} />
        ),
      },
      {
        id: 'star',
        size: 40,
        header: '',
        cell: ({ row }) => <StarDropdown problemId={row.original.id} />,
      },
    ],
    [
      isSignedIn,
      userProgress,
      showTags,
      interviewingioFlag.enabled,
      interviewingioFlag.isLoaded,
    ],
  )

  // TanStack Table instance
  const table = useReactTable({
    data: sortedProblems,
    columns,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
  })

  // Solved count — scoped to BCTCI problems only (matches ProgressSummary)
  const solvedCount = useMemo(() => {
    if (!userProgress || !problems) return 0
    return problems.filter((p) => isSolvedStatus(userProgress.progress[p.id]))
      .length
  }, [userProgress, problems])

  return (
    <Tooltip.Provider delayDuration={150}>
      <div className="flex min-h-[calc(100vh-4rem)]">
        <ProblemListSidebar />
        <div className="problems-table-page mx-auto max-w-5xl flex-1 px-6 py-10">
          {/* Progress Summary + Calendar */}
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <ProgressSummary
              problems={problems ?? []}
              progress={userProgress?.progress}
              className="border-border rounded-lg border p-3 sm:p-4"
            />
            <ActivityStats stats={activityStats} />
          </div>
          <div className="mb-6">
            <ProgressCalendar data={activityData ?? []} />
          </div>
          {/* Topic pills */}
          <TopicPills problems={problems ?? []} />

          {/* Missing lists banner */}
          {!dismissed && missingLists.length > 0 && (
            <div className="mb-4 flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
              <span>
                Some filters reference lists you don&apos;t have — your view may
                differ.
              </span>
              <button
                type="button"
                onClick={() => setDismissed(true)}
                className="ml-4 text-amber-600 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-200"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Toolbar */}
          <div className="mb-4 flex items-center gap-1">
            {/* Search + Sort + Filter */}
            <div className="flex items-center gap-1">
              {/* Search */}
              <ProblemsSearchBar
                value={searchQuery}
                onChange={setSearchQuery}
              />

              {/* Sort + Filter */}
              <span className="flex items-center gap-1">
                <ProblemsSortPopover
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSortChange={(newSortBy, newSortDir) => {
                    setSortBy?.(newSortBy)
                    setSortDir?.(newSortDir)
                  }}
                  showTags={showTags}
                  onToggleTags={() => setShowTags((v) => !v)}
                  sortOptions={PROBLEMS_SORT_POPOVER_OPTIONS}
                  onReset={
                    sortBy !== 'questionId' || sortDir !== 'asc' || showTags
                      ? () => {
                          setSortBy?.('questionId')
                          setSortDir?.('asc')
                          setShowTags(false)
                        }
                      : undefined
                  }
                />
                <ProblemsFilterPopover
                  filters={filters}
                  matchMode={matchMode}
                  onMatchModeChange={setMatchMode}
                  onToggleFilter={toggleFilterValue}
                  onOperatorChange={setFilterOperator}
                  availableTopics={availableTopics}
                  availableDifficulties={availableDifficulties}
                  availableLists={availableLists}
                  onReset={resetFilters}
                />
              </span>
            </div>

            <div className="flex-1" />

            {/* Report bug (auth only) */}
            {isSignedIn && <FeaturebaseBugButton />}

            {/* Solved counter (auth only) */}
            {isSignedIn && userProgress && (
              <span className="text-muted-foreground inline-flex items-center gap-1.5 text-sm">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  className="shrink-0"
                >
                  <circle
                    cx="9"
                    cy="9"
                    r="7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    opacity="0.25"
                  />
                  <circle
                    cx="9"
                    cy="9"
                    r="7"
                    fill="none"
                    stroke="#429539"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 7}
                    strokeDashoffset={
                      2 *
                      Math.PI *
                      7 *
                      (1 - solvedCount / (problems?.length || 1))
                    }
                    transform="rotate(-90 9 9)"
                  />
                </svg>
                {solvedCount}/{problems?.length ?? 0} Solved
              </span>
            )}

            <ShuffleButton
              problems={sortedProblems}
              ariaLabel="Solve a random problem"
            />
          </div>

          {/* Loading state */}
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
            </div>
          ) : (
            /* Table */
            <table className="problems-table w-full">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={cn(
                          'text-muted-foreground py-2 text-left text-xs font-medium',
                          header.column.id === 'title'
                            ? 'px-4'
                            : 'px-2 whitespace-nowrap',
                        )}
                        style={
                          header.column.id !== 'title'
                            ? { width: '1%' }
                            : undefined
                        }
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.length ? (
                  table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      onClick={(e) => {
                        if ((e.target as HTMLElement).closest('a, button'))
                          return
                        navigate(`/editor?problem=${row.original.slug}`)
                      }}
                      className="cursor-pointer transition-colors"
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className={cn(
                            'py-3 text-sm',
                            cell.column.id === 'title'
                              ? 'px-4'
                              : 'px-2 whitespace-nowrap',
                          )}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="text-muted-foreground py-10 text-center"
                    >
                      No problems found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Tooltip.Provider>
  )
}
